<template>
  <div class="quick-actions-container">
    <!-- Greeting Section -->
    <div v-if="effectiveGreeting" class="greeting-section">
      <h3 class="greeting-text">{{ effectiveGreeting }}</h3>
      <p v-if="showContextualPrompt" class="contextual-prompt">
        {{ quickActionsData.contextual_prompt }}
      </p>
    </div>

    <!-- Live Actions Section (if any) -->
    <div v-if="quickActionsData.live_actions && quickActionsData.live_actions.length > 0" class="live-actions-section">
      <h4 class="section-title live-title">
        <i class="fas fa-broadcast-tower text-danger"></i>
        Happening Now
      </h4>
      <div class="live-actions-grid">
        <div 
          v-for="action in quickActionsData.live_actions" 
          :key="`live-${action.type}-${action.title}`"
          class="live-action-card"
          @click="handleLiveAction(action)"
        >
          <div class="live-action-header">
            <span class="live-badge" :class="getLiveBadgeClass(action.type)">
              {{ getLiveBadgeText(action.type) }}
            </span>
            <span v-if="action.starts_in" class="starts-in">{{ action.starts_in }}</span>
          </div>
          <h5 class="live-action-title">{{ action.title }}</h5>
          <p v-if="action.location" class="live-action-location">
            <i class="fas fa-map-marker-alt"></i>
            {{ action.location }}
          </p>
          <p v-if="action.attendees" class="live-action-attendees">
            <i class="fas fa-users"></i>
            {{ action.attendees }} attending
          </p>
          <button class="live-action-btn" @click.stop="handleActionClick(action.action.query)">
            {{ action.action.label }}
          </button>
        </div>
      </div>
    </div>

    <!-- Quick Actions Categories -->
    <div v-if="quickActionsData.categories && quickActionsData.categories.length > 0" class="quick-actions-section">
      <h4 class="section-title">Quick Actions</h4>
      
      <div class="categories-container">
        <div 
          v-for="category in quickActionsData.categories" 
          :key="category.title"
          class="category-section"
        >
          <h5 class="category-title">
            <i v-if="category.icon" :class="getIconClass(category.icon)"></i>
            {{ category.title }}
          </h5>
          
          <div class="actions-grid">
            <button
              v-for="action in category.actions"
              :key="action.id"
              class="quick-action-btn"
              :class="{ 'highlighted': action.is_highlighted }"
              @click="handleActionClick(action.query)"
              :title="action.query"
            >
              <i v-if="action.icon" :class="getIconClass(action.icon)" class="action-icon"></i>
              <span class="action-label">{{ action.label }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading quick actions...</p>
    </div>

    <!-- Error State -->
    <div v-if="error" class="error-state">
      <div class="error-message">
        <i class="fas fa-exclamation-triangle"></i>
        <p>{{ error }}</p>
        <button @click="retryLoad" class="retry-btn">Try Again</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'QuickActions',
  
  props: {
    chatAgentId: {
      type: [Number, String],
      required: true
    },
    attendeeId: {
      type: [Number, String],
      default: null
    },
    displayConfig: {
      type: Object,
      default: null
    }
  },
  
  data() {
    return {
      quickActionsData: {},
      loading: false,
      error: null
    };
  },
  
  computed: {
    /**
     * Get the effective greeting - prioritize welcome_message from display_config
     */
    effectiveGreeting() {
      return this.displayConfig?.welcome_message || this.quickActionsData.greeting;
    },
    
    /**
     * Show contextual prompt only when there's no welcome_message
     */
    showContextualPrompt() {
      return !this.displayConfig?.welcome_message && this.quickActionsData.contextual_prompt;
    }
  },
  
  mounted() {
    this.loadQuickActions();
  },
  
  watch: {
    chatAgentId() {
      this.loadQuickActions();
    },
    attendeeId() {
      this.loadQuickActions();
    }
  },
  
  methods: {
    async loadQuickActions() {
      this.loading = true;
      this.error = null;
      
      try {
        const params = {};
        if (this.attendeeId) {
          params.attendee_id = this.attendeeId;
        }
        
        const response = await axios.get(`/api/v1/chat-agents/${this.chatAgentId}/quick-actions`, {
          params
        });
        
        if (response.data.success) {
          this.quickActionsData = response.data.data;
        } else {
          throw new Error(response.data.message || 'Failed to load quick actions');
        }
      } catch (error) {
        console.error('Error loading quick actions:', error);
        this.error = error.response?.data?.message || 'Failed to load quick actions. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    
    handleActionClick(query) {
      this.$emit('action-selected', query);
    },
    
    handleLiveAction(action) {
      // Handle live action click - could show more details or trigger action
      if (action.action && action.action.query) {
        this.handleActionClick(action.action.query);
      }
    },
    
    retryLoad() {
      this.loadQuickActions();
    },
    
    getIconClass(icon) {
      // Map icon names to FontAwesome classes
      const iconMap = {
        'clock': 'fas fa-clock',
        'calendar': 'fas fa-calendar',
        'upcoming': 'fas fa-clock',
        'bookmark': 'fas fa-bookmark',
        'star': 'fas fa-star',
        'search': 'fas fa-search',
        'building': 'fas fa-building',
        'tag': 'fas fa-tag',
        'map': 'fas fa-map',
        'coffee': 'fas fa-coffee',
        'users': 'fas fa-users',
        'location': 'fas fa-map-marker-alt',
        'alert': 'fas fa-exclamation-triangle',
        'info': 'fas fa-info-circle',
        'wifi': 'fas fa-wifi',
        'fire': 'fas fa-fire',
        'handshake': 'fas fa-handshake',
        'link': 'fas fa-link',
        'magic': 'fas fa-magic',
        'trending': 'fas fa-trending-up'
      };
      
      return iconMap[icon] || 'fas fa-question-circle';
    },
    
    getLiveBadgeClass(type) {
      return {
        'live-session': 'live-badge-red',
        'upcoming_session': 'live-badge-orange'
      }[type] || 'live-badge-blue';
    },
    
    getLiveBadgeText(type) {
      return {
        'live_session': 'LIVE',
        'upcoming_session': 'SOON'
      }[type] || 'ACTIVE';
    }
  }
};
</script>

<style scoped>
.quick-actions-container {
  padding: 1.5rem;
  max-width: 100%;
}

.greeting-section {
  text-align: center;
  margin-bottom: 2rem;
}

.greeting-text {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.contextual-prompt {
  color: #718096;
  font-size: 0.9rem;
  margin: 0;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.live-title {
  color: #e53e3e;
}

.live-actions-section {
  margin-bottom: 2rem;
}

.live-actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.live-action-card {
  background: linear-gradient(135deg, #fff5f5 0%, #fed7d7 100%);
  border: 2px solid #feb2b2;
  border-radius: 12px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.live-action-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(229, 62, 62, 0.15);
  border-color: #fc8181;
}

.live-action-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.live-badge {
  font-size: 0.7rem;
  font-weight: bold;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  color: white;
}

.live-badge-red {
  background: #e53e3e;
}

.live-badge-orange {
  background: #dd6b20;
}

.live-badge-blue {
  background: #3182ce;
}

.starts-in {
  font-size: 0.8rem;
  color: #e53e3e;
  font-weight: 600;
}

.live-action-title {
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 0.5rem 0;
  line-height: 1.3;
}

.live-action-location,
.live-action-attendees {
  font-size: 0.85rem;
  color: #4a5568;
  margin: 0.25rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.live-action-btn {
  background: #e53e3e;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
  margin-top: 0.5rem;
  width: 100%;
}

.live-action-btn:hover {
  background: #c53030;
}

.quick-actions-section {
  margin-bottom: 1.5rem;
}

.categories-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.category-section {
  background: #f7fafc;
  border-radius: 8px;
  padding: 1rem;
}

.category-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #2d3748;
  margin: 0 0 0.75rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 0.75rem;
}

.quick-action-btn {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;
  min-height: 80px;
  justify-content: center;
}

.quick-action-btn:hover {
  background: #f7fafc;
  border-color: #4299e1;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(66, 153, 225, 0.15);
}

.quick-action-btn.highlighted {
  background: linear-gradient(135deg, #ebf8ff 0%, #bee3f8 100%);
  border-color: #4299e1;
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
}

.action-icon {
  font-size: 1.2rem;
  color: #4299e1;
}

.action-label {
  font-size: 0.85rem;
  font-weight: 500;
  color: #2d3748;
  line-height: 1.2;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 2rem;
}

.spinner {
  border: 2px solid #e2e8f0;
  border-top: 2px solid #4299e1;
  border-radius: 50%;
  width: 2rem;
  height: 2rem;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  color: #e53e3e;
}

.retry-btn {
  background: #4299e1;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 1rem;
}

.retry-btn:hover {
  background: #3182ce;
}

@media (max-width: 768px) {
  .quick-actions-container {
    padding: 1rem;
  }
  
  .live-actions-grid {
    grid-template-columns: 1fr;
  }
  
  .actions-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .quick-action-btn {
    min-height: 70px;
    padding: 0.5rem;
  }
}
</style>